import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Vector3 } from 'three';

import { isValidNumber } from '../../../../../../../utils/math/number';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool, PlaceMode } from '../../../../../../../viewer/tools/design-tool';
import { ViewSettings } from '../../../../../../../viewer/view-settings';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { OrderedList } from '../../../../../ordered-list/ordered-list';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';
import { PositionInput } from '../../../components/position-input';

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

export const PositionSection = (props: Props) => {
  const { t } = useTranslation();
  const [placeMode, setPlaceMode] = React.useState(props.tool.placeMode);
  const [length, setLength] = React.useState(props.tool.snapValues.length.toString());
  const [isCadViewer] = React.useState(ViewSettings.instance.isCadViewer);

  const onPlaceModeChanged = (newValue: PlaceMode) => {
    setPlaceMode(newValue);
    props.tool.onPlaceModeChanged(newValue);
  };

  const onLengthChanged = (newValue: string) => {
    setLength(newValue);

    const value = parseFloat(newValue);

    if (isValidNumber(value)) {
      props.tool.onSnapValuesChanged({ length: value });
    }
  };

  return (
    <CollapsableSection
      expanded={props.expanded}
      title="POSITION"
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { placeMode: state })
      }
    >
      <OptionLabels
        active={placeMode}
        itemRows={[
          [
            {
              title: t('section.position.freeHand.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Free,
              tooltipText: (
                <Trans
                  components={{ bold: <strong /> }}
                  i18nKey="section.position.freeHand.tooltip"
                  ns="skyviewDesign"
                />
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.Free),
            },
            {
              title: t('section.position.length.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Length,
              tooltipText: (
                <Trans
                  components={{ bold: <strong /> }}
                  i18nKey="section.position.length.tooltip"
                  ns="skyviewDesign"
                />
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.Length),
              content: (
                <LabelledContainer
                  key={PlaceMode.Length}
                  text={t('section.position.length.inputTitle', { ns: 'skyviewDesign' })}
                >
                  {(formElementId) => (
                    <TextBox
                      autoFocus={true}
                      defaultValue={length}
                      id={formElementId()}
                      step={0.01}
                      type="number"
                      onChange={(e) => onLengthChanged(e.target.value)}
                    />
                  )}
                </LabelledContainer>
              ),
            },
          ],
          [
            {
              title: t('section.position.freePerpendicular.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.FreePerpendicular,
              tooltipText: t('section.position.freePerpendicular.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => onPlaceModeChanged(PlaceMode.FreePerpendicular),
            },
            {
              title: t('section.position.lengthPerpendicular.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.LengthPerpendicular,
              tooltipText: t('section.position.lengthPerpendicular.tooltip', {
                ns: 'skyviewDesign',
              }),
              onSelect: () => onPlaceModeChanged(PlaceMode.LengthPerpendicular),
              content: (
                <LabelledContainer
                  key={PlaceMode.LengthPerpendicular}
                  text={t('section.position.lengthPerpendicular.inputTitle', {
                    ns: 'skyviewDesign',
                  })}
                >
                  {(formElementId) => (
                    <TextBox
                      defaultValue={length}
                      id={formElementId()}
                      step={0.01}
                      type="number"
                      onChange={(e) => onLengthChanged(e.target.value)}
                    />
                  )}
                </LabelledContainer>
              ),
            },
          ],
          [
            {
              title: t('section.position.coordinate.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.Position,
              tooltipText: (
                <Stack direction="column" spacing={1}>
                  <OrderedList
                    items={[
                      t('section.position.coordinate.tooltip.list.item1', { ns: 'skyviewDesign' }),
                      t('section.position.coordinate.tooltip.list.item2', { ns: 'skyviewDesign' }),
                      t('section.position.coordinate.tooltip.list.item3', { ns: 'skyviewDesign' }),
                    ]}
                  />
                  <span>
                    <Trans
                      components={{ bold: <strong />, br: <br /> }}
                      i18nKey="section.position.coordinate.tooltip.importFromCsv"
                      ns="skyviewDesign"
                    />
                  </span>
                </Stack>
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.Position),
              content: (
                <PositionInput
                  // Note: Local X and Y coordinates are flipped compared to what is displayed.
                  defaultValue={{
                    x: props.tool.snapValues.position.y,
                    y: props.tool.snapValues.position.x,
                    z: props.tool.snapValues.position.z,
                  }}
                  display={[
                    t('section.position.coordinate.input.northing', { ns: 'skyviewDesign' }),
                    t('section.position.coordinate.input.easting', { ns: 'skyviewDesign' }),
                    t('section.position.coordinate.input.height', { ns: 'skyviewDesign' }),
                  ]}
                  onValueChanged={(v) =>
                    props.tool.onSnapValuesChanged({
                      position: new Vector3(v.y, v.x, v.z),
                    })
                  }
                />
              ),
            },
            {
              title: t('section.position.offset.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.OffsetXYZ,
              onSelect: () => onPlaceModeChanged(PlaceMode.OffsetXYZ),
              tooltipText: (
                <Trans
                  components={{ bold: <strong />, br: <br /> }}
                  i18nKey="section.position.offset.tooltip"
                  ns="skyviewDesign"
                />
              ),
              content: (
                <PositionInput
                  // Note: Local X and Y coordinates are flipped compared to what is displayed.
                  defaultValue={{
                    x: props.tool.snapValues.xyzOffset.y,
                    y: props.tool.snapValues.xyzOffset.x,
                    z: props.tool.snapValues.xyzOffset.z,
                  }}
                  onValueChanged={(v) =>
                    props.tool.onSnapValuesChanged({
                      xyzOffset: new Vector3(v.y, v.x, v.z),
                    })
                  }
                />
              ),
            },
          ],
          isCadViewer
            ? []
            : [
                {
                  title: t('section.position.intersectPointCloud.text', { ns: 'skyviewDesign' }),
                  value: PlaceMode.IntersectPointcloud,
                  tooltipText: (
                    <Trans
                      components={{ br: <br /> }}
                      i18nKey="section.position.intersectPointCloud.tooltip"
                      ns="skyviewDesign"
                    />
                  ),
                  onSelect: () => onPlaceModeChanged(PlaceMode.IntersectPointcloud),
                },
              ],
          [
            {
              title: t('section.position.intersectObjects.text', { ns: 'skyviewDesign' }),
              value: PlaceMode.IntersectObjects,
              tooltipText: (
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="section.position.intersectObjects.tooltip"
                  ns="skyviewDesign"
                />
              ),
              onSelect: () => onPlaceModeChanged(PlaceMode.IntersectObjects),
            },
          ],
        ]}
      />
    </CollapsableSection>
  );
};
